import React, { useEffect, useState } from 'react';
import { Provider } from 'react-redux';
import DOMPurify from 'dompurify';
import messageLogo from "../../../../assets/images/signin_message_logo.svg";
import { store } from '../../../redux/store';
import HubLayout from './HubLayout';
import { http_post } from '../utils/api_client';
import { API_ENDPOINTS } from '../utils/endpoints';

const DEFAULT_EXPIRATION_MINUTES = 20;
const RESEND_COOLDOWN_SECONDS = 10;
const EMAIL_REGEX = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;

const parseCustomCss = (rawCss) => {
  if (!rawCss) return null;

  try {
    const parsedCss = JSON.parse(rawCss);
    return typeof parsedCss === 'string' ? DOMPurify.sanitize(parsedCss) : null;
  } catch (_error) {
    return DOMPurify.sanitize(rawCss);
  }
};

const SendIcon = () => (
  <svg aria-hidden="true" viewBox="0 0 24 24">
    <path d="M21.8 3.6a1 1 0 0 0-1-.18L3.9 10.1a1 1 0 0 0 .08 1.88l6.53 1.97 1.97 6.53a1 1 0 0 0 .91.71h.07a1 1 0 0 0 .9-.58L22 4.55a1 1 0 0 0-.2-.95Z" fill="currentColor" />
  </svg>
);

const PasswordlessSignIn = ({ design, expiresInMinutes = DEFAULT_EXPIRATION_MINUTES, linkExpired = false }) => {
  const [email, setEmail] = useState('');
  const [isSendingLink, setIsSendingLink] = useState(false);
  const [emailError, setEmailError] = useState(null);
  const [requestError, setRequestError] = useState(null);
  const [linkSent, setLinkSent] = useState(false);
  const [resendCooldown, setResendCooldown] = useState(0);

  const brandName = design?.details?.company_name?.trim() || '';
  const isCustomized = Boolean(design?.logo_file_name && brandName);
  const customCss = parseCustomCss(design?.css);
  const resendLocked = isSendingLink || resendCooldown > 0;
  const showExpiredState = linkExpired && !linkSent;

  useEffect(() => {
    if (resendCooldown <= 0) return undefined;

    const timerId = window.setTimeout(() => {
      setResendCooldown((currentCooldown) => Math.max(currentCooldown - 1, 0));
    }, 1000);

    return () => window.clearTimeout(timerId);
  }, [resendCooldown]);

  const validateEmail = (value) => {
    if (!value.trim()) return 'Email is required';
    if (!EMAIL_REGEX.test(value)) return 'Invalid email address';
    return null;
  };

  const handleEmailChange = (event) => {
    const nextEmail = event.target.value;
    setEmail(nextEmail);
    setRequestError(null);

    if (!nextEmail.trim()) {
      setEmailError(null);
      return;
    }

    setEmailError(validateEmail(nextEmail));
  };

  const sendMagicLink = async () => {
    const normalizedEmail = email.trim();
    const validationMessage = validateEmail(normalizedEmail);

    if (validationMessage) {
      setEmailError(validationMessage);
      return;
    }

    const companyName = window.location.pathname.split('/')[1];
    const returnTo = new URLSearchParams(window.location.search).get('return_to');
    const payload = { email: normalizedEmail, company_name: companyName };

    if (returnTo) payload.return_to = returnTo;

    setIsSendingLink(true);
    setEmailError(null);
    setRequestError(null);

    try {
      await http_post(API_ENDPOINTS.SEND_MAGIC_LINK, payload);
      setLinkSent(true);
    } catch (error) {
      console.error(error);
      setRequestError('Unable to send a magic link right now. Please try again.');
    } finally {
      setIsSendingLink(false);
    }
  };

  const handleGetMagicLink = async (event) => {
    event.preventDefault();
    await sendMagicLink();
  };

  const handleBackToSignIn = () => {
    setLinkSent(false);
    setRequestError(null);
  };

  const handleResend = async () => {
    setResendCooldown(RESEND_COOLDOWN_SECONDS);
    await sendMagicLink();
  };

  return (
    <Provider store={store}>
      <HubLayout hideHeader={true}>
        <div className={`un-passwordless ${isCustomized ? 'un-passwordless--customized' : 'un-passwordless--default'}`}>
          <div className="un-passwordless__shell">
            <section className="un-passwordless__card" aria-live="polite">
              <header className="un-passwordless__hero">
                {isCustomized && (
                  <div className="un-passwordless__brand">
                    <img
                      className="un-passwordless__brand-logo"
                      src={design.logo}
                      alt={`${brandName} logo`}
                    />
                    <span className="un-passwordless__brand-name">{brandName}</span>
                  </div>
                )}
                <h1 className="un-passwordless__title">
                  {linkSent ? 'Check your inbox' : showExpiredState ? 'Link expired' : 'Welcome Back!'}
                </h1>
                <p className="un-passwordless__subtitle">
                  {linkSent
                    ? 'We’ve sent a secure magic link to access your onboarding path.'
                    : showExpiredState
                      ? 'Your previous sign-in link expired. Enter your email to receive a new secure link and continue where you left off.'
                      : 'Enter your email to access your personalized onboarding path.'}
                </p>
              </header>

              <div className="un-passwordless__body">
                {linkSent ? (
                  <>
                    <div className="un-passwordless__illustration">
                      <img src={messageLogo} alt="Email sent" />
                    </div>
                    <p className="un-passwordless__body-copy">
                      Click the link in the email we just sent to sign in instantly.
                    </p>
                    <div className="un-passwordless__expiry">
                      Link expires in {expiresInMinutes} minutes
                    </div>
                    {requestError && (
                      <p className="un-passwordless__error">{requestError}</p>
                    )}
                    <button
                      type="button"
                      className="un-passwordless__primary-action"
                      onClick={handleBackToSignIn}
                      disabled={isSendingLink}
                    >
                      Back to Sign in
                    </button>
                    <p className="un-passwordless__secondary-copy">
                      Didn&apos;t receive the email?{' '}
                      <button
                        type="button"
                        className="un-passwordless__link-button"
                        onClick={handleResend}
                        disabled={resendLocked}
                      >
                        {resendLocked && <span className="un-passwordless__link-spinner" aria-hidden="true" />}
                        <span>
                          {isSendingLink
                            ? 'Resending...'
                            : resendCooldown > 0
                              ? `Resend available in ${resendCooldown}s`
                              : 'Click to resend'}
                        </span>
                      </button>
                    </p>
                  </>
                ) : (
                  <form className="un-passwordless__form" onSubmit={handleGetMagicLink}>
                    {showExpiredState && (
                      <div className="un-passwordless__notice" role="status">
                        Your sign-in link is no longer valid. Request a new magic link to continue your onboarding path.
                      </div>
                    )}
                    <label className="un-passwordless__label" htmlFor="passwordless-email">
                      Email Address
                    </label>
                    <input
                      id="passwordless-email"
                      type="email"
                      name="email"
                      className="un-passwordless__input"
                      placeholder="your.email@company.com"
                      autoComplete="email"
                      value={email}
                      onChange={handleEmailChange}
                      required
                    />
                    {(emailError || requestError) && (
                      <p className="un-passwordless__error">{emailError || requestError}</p>
                    )}
                    <button
                      className="un-passwordless__primary-action"
                      type="submit"
                      disabled={isSendingLink || !email.trim() || Boolean(emailError)}
                    >
                      <SendIcon />
                      <span>{isSendingLink ? 'Sending...' : showExpiredState ? 'Send New Magic Link' : 'Send Magic Link'}</span>
                    </button>
                    <p className="un-passwordless__helper">
                      We&apos;ll send a secure link to your email that will sign you in automatically.
                    </p>
                  </form>
                )}
              </div>
            </section>
          </div>
          {customCss && <style dangerouslySetInnerHTML={{ __html: customCss }} />}
        </div>
      </HubLayout>
    </Provider>
  );
};

export default PasswordlessSignIn;
